import { keyframes } from '@mui/material';
import { CSSObject, styled } from '@mui/material/styles';

import { shouldForwardProps } from '../../utils/mui-styled.utils';
import { USER_INPUT_HEIGHT } from '../../utils/sizes.utils';
import { WidgetLoading } from '../widget-loading/widget-loading.component';

interface BotMessagesStyledProps {
	paddingChatValue: number;
	isSingleBotTextMessage: boolean;
	isLoading: boolean;
	isUndo: boolean;
}

interface UserMessageStyledProps {
	isLoading: boolean;
	isUndo: boolean;
}

interface RootFlags {
	overflowHidden: boolean;
}

const calcPadding = (percent: number, px: number) => {
	const sign = px >= 0 ? '+' : '-';
	const abs = Math.abs(px);
	return `calc(${percent}% ${sign} ${abs}px) !important`;
};

export const StyledWidgetContentRoot = styled('div', {
	shouldForwardProp: shouldForwardProps(['overflowHidden']),
})<RootFlags>(
	({
		theme: {
			palette: { colorScheme },
		},
		overflowHidden,
	}) => ({
		width: '100%',
		height: '100%',
		overflowX: 'hidden',
		overflowY: overflowHidden ? 'hidden' : 'auto',
		'@supports (overflow-y: overlay)': {
			overflowY: overflowHidden ? 'hidden' : 'auto',
		},
		position: 'relative',
		flex: 1,
		flexDirection: 'column',
		justifyContent: 'space-between',
		zIndex: 1,
		'&::-webkit-scrollbar': {
			display: 'block',
			width: 5,
		},
		'&::-webkit-scrollbar-track': {
			background: 'transparent',
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: colorScheme.colorVariables.colorBorder,
			borderRadius: 5,
			borderRight: 'none',
			borderLeft: 'none',
		},
		'&::-webkit-scrollbar-track-piece:end': {
			background: 'transparent',
			marginBottom: 96,
		},

		...colorScheme.customStyle.content,
	}),
);

export const StyledWidgetContentBotMessages = styled('div', {
	shouldForwardProp: (prop) =>
		!['paddingChatValue', 'isSingleBotTextMessage', 'isLoading', 'isUndo'].includes(prop as string),
})<BotMessagesStyledProps>(({
	theme: {
		palette: {
			sizes,
			transitionConfig: { noAnimationTransition },
		},
	},
	paddingChatValue,
	isSingleBotTextMessage,
	isLoading,
	isUndo,
}): CSSObject => {
	const styles: CSSObject = {
		position: 'relative',
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		marginTop: sizes.size_8,
		justifyContent: 'center',
		minHeight: `calc(100% - ${USER_INPUT_HEIGHT}px - ${sizes.size_2}px)`,
		top: 0,
		'& > div:first-of-type': { marginTop: 'auto' },
		'& > div:last-of-type': { marginTop: 'auto' },
		paddingBottom: calcPadding(sizes.size_1, paddingChatValue),
	};

	if (isSingleBotTextMessage) {
		Object.assign(styles, {
			'& > div:first-of-type, & > div:last-of-type, & > div': { marginTop: 0 },
		});
	}

	if (!isLoading) {
		Object.assign(styles, {
			opacity: 1,
			top: 0,
			transition: noAnimationTransition ? 'initial' : 'opacity 600ms',
		});
	} else if (isUndo) {
		Object.assign(styles, {
			opacity: 0,
			top: '4%',
			transition: noAnimationTransition ? 'initial' : 'top 800ms, opacity 800ms',
			pointerEvents: 'none',
		});
	} else {
		Object.assign(styles, {
			opacity: 0,
			top: '-4%',
			transition: noAnimationTransition ? 'initial' : 'top 800ms, opacity 800ms',
		});
	}

	return styles;
});

const enterPreviousLoadAnimation = keyframes`
    0%   { opacity: 0; transform: translateY(-16%); }
    100% { opacity: 1; transform: translateY(0);     }
  `;

const enterNextLoadAnimation = keyframes`
    0%   { opacity: 0; transform: translateY(16%); }
    100% { opacity: 1; transform: translateY(0);   }
  `;

export const StyledWidgetContentUserMessage = styled('div', {
	shouldForwardProp: shouldForwardProps(['isLoading', 'isUndo']),
})<UserMessageStyledProps>(({
	theme: {
		palette: {
			sizes,
			transitionConfig: { noAnimationTransition },
		},
	},
	isLoading,
	isUndo,
}) => {
	const baseStyles: CSSObject = {
		position: 'absolute',
		width: '100%',
		padding: `${sizes.size_1}px 0 0`,
		zIndex: 1,
		background: 'transparent',
	};
	if (!isLoading) {
		return {
			...baseStyles,
			opacity: 1,
			top: 0,
			transition: noAnimationTransition ? 'initial' : 'opacity 600ms',
		};
	}
	if (isUndo) {
		return noAnimationTransition
			? {
					...baseStyles,
					opacity: 1,
					transform: 'translateY(0)',
				}
			: {
					...baseStyles,
					animation: `${enterPreviousLoadAnimation} 400ms`,
				};
	}
	return noAnimationTransition
		? {
				...baseStyles,
				opacity: 1,
				transform: 'translateY(0)',
			}
		: {
				...baseStyles,
				animation: `${enterNextLoadAnimation} 400ms`,
			};
});

export const StyledWidgetContentLoadingAnimation = styled('div', {
	shouldForwardProp: shouldForwardProps(['isUndo']),
})<{ isUndo: boolean }>(({ isUndo }) => ({
	zIndex: -1,
	position: 'absolute',
	inset: 0,
	width: '100%',
	overflow: 'hidden',
	opacity: 0,
	transform: `translateY(${isUndo ? '-4%' : '4%'})`,
}));

export const StyledWidgetContentLoading = styled(WidgetLoading, {
	shouldForwardProp: shouldForwardProps(['paddingChatValue']),
})<{ paddingChatValue: number }>(
	({
		theme: {
			palette: { sizes },
		},
		paddingChatValue,
	}) => ({
		paddingBottom: calcPadding(sizes.size_1, paddingChatValue),
	}),
);
